import React from 'react';
import { motion } from 'framer-motion';
import { Layers, ArrowRight, Zap, Check } from 'lucide-react';
import { Box, Flex, Text } from '@chakra-ui/react';

const Infographic = () => {
  const nodes = [
    { id: 'jira', label: 'Jira', url: 'https://cdn.simpleicons.org/jira/0052CC', color: '#0052CC', angle: -90 },
    { id: 'clickup', label: 'ClickUp', url: 'https://cdn.simpleicons.org/clickup/7B68EE', color: '#7B68EE', angle: -18 },
    { id: 'notion', label: 'Notion', url: 'https://cdn.simpleicons.org/notion/000000', color: '#000000', angle: 54, isComingSoon: true },
    { id: 'slack', label: 'Slack', url: 'https://cdn.jsdelivr.net/npm/simple-icons@v11/icons/slack.svg', color: '#4A154B', angle: 126, isComingSoon: true },
    { id: 'asana', label: 'Asana', url: 'https://cdn.simpleicons.org/asana/F06A6A', color: '#F06A6A', angle: 198, isComingSoon: true },
  ];

  return (
    <Box
      as="section"
      id="infographic"
      bg="landing.bg"
      py={{ base: 24, md: 32 }}
      fontFamily="body"
      overflow="hidden"
      position="relative"
    >
      {/* Background Decor */}
      <Box 
        position="absolute" 
        top="-10%" right="-10%" 
        w="50%" h="50%" 
        bg="radial-gradient(circle, rgba(37, 99, 235, 0.03) 0%, transparent 70%)" 
        pointerEvents="none" 
      />
      <Box 
        position="absolute" 
        bottom="-10%" left="-10%" 
        w="40%" h="40%" 
        bg="radial-gradient(circle, rgba(34, 211, 238, 0.04) 0%, transparent 70%)" 
        pointerEvents="none" 
      />

      <Box maxW="1200px" mx="auto" px={{ base: 5, md: 8 }} position="relative" zIndex={1}>
        <Box textAlign="center" mb={24}>
          <Flex
            display="inline-flex"
            alignItems="center"
            px={3} py={1.5}
            borderRadius="full"
            bg="brand.subtle"
            borderWidth="1px"
            borderColor="landing.border"
            mb={4}
            mx="auto"
          >
            <Box color="brand.primary" display="inline-flex"><Zap size={13} /></Box>
            <Text fontSize="xs" fontWeight="800" color="brand.primary" ml={1.5} letterSpacing="0.05em">PLATFORM FOUNDATION</Text>
          </Flex>
          <Text
            as="h2"
            fontSize={{ base: '3xl', md: '5xl' }}
            fontWeight="900"
            color="text.main"
            letterSpacing="-0.03em"
            mb={4}
          >
            Built to Expand <Box as="span" color="brand.primary">Across Your Workflow</Box>
          </Text>
          <Text fontSize="lg" color="text.secondary" maxW="650px" mx="auto" lineHeight="1.7">
            DatLynk is live today for Jira and ClickUp, with a structure-aware foundation designed for broader cross-tool sync over time.{' '}
            <Box as="span" display="block" mt={4} color="brand.primary" fontWeight="800" fontSize="md" letterSpacing="0.02em">
              START WITH JIRA AND CLICKUP. EXPAND WITH CONFIDENCE.
            </Box>
          </Text>
        </Box>

        <Flex
          direction={{ base: 'column', lg: 'row' }}
          alignItems="center"
          justifyContent="center"
          gap={{ base: 16, lg: 20 }}
        >
          {/* Central Hub Visualization */}
          <Box position="relative" w={{ base: '296px', md: '560px' }} h={{ base: '296px', md: '560px' }}>
            {/* Background Glow */}
            <Box 
              position="absolute" 
              top="0" 
              bottom="0" 
              left="0" 
              right="0"
              margin="auto"
              w="150%" 
              h="150%" 
              bg="bg.glow"
              borderRadius="full"
              pointerEvents="none"
              opacity={0.6}
              zIndex={0}
            />

            {/* Pulsing rings */}
            {[1, 2, 3].map((i) => (
              <Box
                key={i}
                position="absolute"
                top="0"
                bottom="0"
                left="0"
                right="0"
                margin="auto"
                w={`${i * 28}%`}
                h={`${i * 28}%`}
                borderRadius="full"
                border="1px solid"
                borderColor="brand.primary"
                style={{ animation: `pulse-ring-scale ${2 + i}s infinite ease-in-out` }}
              />
            ))}

            {/* Hub Core */}
            <Box
              position="absolute"
              top="50%"
              left="50%"
              transform="translate(-50%, -50%)"
              zIndex={10}
            >
              <motion.div
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 4, repeat: Infinity }}
              >
                <Box
                  w={{ base: '68px', md: '120px' }}
                  h={{ base: '68px', md: '120px' }}
                  bg="brand.primary"
                  borderRadius={{ base: '2xl', md: '3xl' }}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  boxShadow={{ base: '0 16px 40px rgba(37, 99, 235, 0.32), 0 0 0 6px rgba(37, 99, 235, 0.12)', md: '0 24px 64px rgba(37, 99, 235, 0.4), 0 0 0 8px rgba(37, 99, 235, 0.15)' }}
                  position="relative"
                >
                  <Box w={{ base: '32px', md: '60px' }} h={{ base: '32px', md: '60px' }} position="relative">
                    {/* Abstract DatLynk Logo Center */}
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
                      <path d="M12 2L2 12L12 22L22 12L12 2Z" stroke="white" strokeWidth="2" strokeLinejoin="round"/>
                      <path d="M12 8L8 12L12 16L16 12L12 8Z" fill="white"/>
                    </svg>
                  </Box>
                  <Box
                    position="absolute"
                    bottom={{ base: '-9px', md: '-12px' }}
                    bg="success.500"
                    px={{ base: 2, md: 3 }}
                    py={{ base: 0.5, md: 1 }}
                    borderRadius="full"
                    boxShadow={{ base: 'sm', md: 'md' }}
                    border="1px solid"
                    borderColor="success.600"
                  >
                    <Text fontSize={{ base: '8px', md: '10px' }} fontWeight="900" color="white" letterSpacing="0.04em" whiteSpace="nowrap">
                      AVAILABLE NOW
                    </Text>
                  </Box>
                </Box>
              </motion.div>
            </Box>

            {/* Satellites */}
            {nodes.map((node, i) => {
              // Calculate responsive radius
              const radiusBase = 120;
              const radiusMd = 180;
              
              const xBase = Math.cos((node.angle * Math.PI) / 180) * radiusBase;
              const yBase = Math.sin((node.angle * Math.PI) / 180) * radiusBase;
              
              const xMd = Math.cos((node.angle * Math.PI) / 180) * radiusMd;
              const yMd = Math.sin((node.angle * Math.PI) / 180) * radiusMd;

              return (
                <Box
                  key={node.id}
                  position="absolute"
                  top={{ base: `calc(50% + ${yBase}px)`, md: `calc(50% + ${yMd}px)` }}
                  left={{ base: `calc(50% + ${xBase}px)`, md: `calc(50% + ${xMd}px)` }}
                  transform="translate(-50%, -50%)"
                  zIndex={5}
                >
                  <motion.div
                    initial={{ opacity: 0, scale: 0 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.05 + 0.1, duration: 0.4 }}
                  >
                    <Flex direction="column" alignItems="center" gap={3}>
                      <Box
                        w={{ base: "48px", md: "64px" }} 
                        h={{ base: "48px", md: "64px" }}
                        borderRadius="2xl"
                        bg="landing.card"
                        borderWidth="1px"
                        borderColor="landing.border"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        boxShadow="shadows.card"
                        position="relative"
                        _hover={{ transform: 'scale(1.1)', borderColor: node.color, boxShadow: `0 10px 25px ${node.color}30` }}
                        transition="all 200ms"
                        className="group"
                      >
                        <Box bg="white" p={1.5} borderRadius="lg" display="flex" alignItems="center" justifyContent="center">
                           <img 
                             src={node.url} 
                             alt={node.label} 
                             style={{ width: '24px', height: '24px', objectFit: 'contain' }} 
                           />
                        </Box>
                        <Box 
                          position="absolute" inset="-4px" 
                          borderRadius="2xl" border="1px solid" 
                          borderColor={node.color} opacity={0} 
                          transition="all 200ms"
                          _groupHover={{ inset: "-6px", opacity: 0.4 }}
                        />
                        {node.isComingSoon && (
                          <Box
                            position="absolute"
                            top={{ base: '-8px', md: '-10px' }}
                            right={{ base: '-12px', md: '-14px' }}
                            px={{ base: 1.5, md: 2 }}
                            py={0.5}
                            borderRadius="full"
                            bg="brand.subtle"
                            borderWidth="1px"
                            borderColor="brand.muted"
                            boxShadow="sm"
                          >
                            <Text
                              fontSize={{ base: '6px', md: '7px' }}
                              fontWeight="900"
                              color="brand.primary"
                              letterSpacing="0.08em"
                              textTransform="uppercase"
                              whiteSpace="nowrap"
                              lineHeight="1"
                            >
                              Coming soon
                            </Text>
                          </Box>
                        )}
                      </Box>
                      <Box bg="landing.surface" px={2.5} py={1} borderRadius="md" border="1px solid" borderColor="landing.border" whiteSpace="nowrap">
                         <Text fontSize={{ base: "9px", md: "11px" }} fontWeight="800" color="text.main" letterSpacing="0.05em">
                          {node.label.toUpperCase()}
                        </Text>
                      </Box>
                    </Flex>
                  </motion.div>
                </Box>
              );
            })}

            {/* Connection Lines (Responsive SVG) */}
            <Box as="svg" position="absolute" top={0} left={0} w="100%" h="100%" pointerEvents="none" zIndex={1}>
               {nodes.map((node) => {
                 const radiusBase = 120;
                 const radiusMd = 180;
                 
                 const xBase = Math.cos((node.angle * Math.PI) / 180) * radiusBase;
                 const yBase = Math.sin((node.angle * Math.PI) / 180) * radiusBase;
                 
                 const xMd = Math.cos((node.angle * Math.PI) / 180) * radiusMd;
                 const yMd = Math.sin((node.angle * Math.PI) / 180) * radiusMd;

                 return (
                   <React.Fragment key={`line-${node.id}`}>
                     <Box 
                       as="line" 
                       x1="50%" y1="50%" 
                       x2={`calc(50% + ${xBase}px)`} y2={`calc(50% + ${yBase}px)`} 
                       stroke="var(--chakra-colors-brand-primary)" 
                       opacity={0.3}
                       strokeWidth="2" 
                       strokeDasharray="6 6"
                       display={{ base: 'block', md: 'none' }}
                     />
                     <Box 
                       as="line" 
                       x1="50%" y1="50%" 
                       x2={`calc(50% + ${xMd}px)`} y2={`calc(50% + ${yMd}px)`} 
                       stroke="var(--chakra-colors-brand-primary)" 
                       opacity={0.3}
                       strokeWidth="2" 
                       strokeDasharray="6 6"
                       display={{ base: 'none', md: 'block' }}
                     />
                   </React.Fragment>
                 )
               })}
            </Box>
          </Box>

          {/* Hierarchy Details Panel */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            <Box
              p={8}
              w="100%"
              maxW="420px"
              borderRadius="2xl"
              bg="landing.card"
              borderWidth="1px"
              borderColor="landing.border"
              boxShadow="shadows.card"
              position="relative"
              overflow="hidden"
            >
              {/* Top Accent Line */}
              <Box position="absolute" top="0" left="0" right="0" h="4px" bg="brand.primary" />

              <Flex alignItems="center" gap={4} mb={6}>
                <Box w={12} h={12} bg="brand.subtle" borderRadius="xl" display="flex" alignItems="center" justifyContent="center">
                   <Box color="brand.primary" display="inline-flex"><Layers size={24} /></Box>
                </Box>
                <Box>
                  <Text fontSize="md" fontWeight="900" color="brand.primary" letterSpacing="0.02em">JIRA + CLICKUP LIVE</Text>
                  <Text fontSize="11px" color="text.secondary" fontWeight="700" letterSpacing="0.05em">STRUCTURE-FIRST SYNC</Text>
                </Box>
              </Flex>

              <Flex direction="column" gap={4}>
                {[
                  { from: 'JIRA EPIC', to: 'CLICKUP LIST', color: 'brand.primary' },
                  { from: 'STATUS', to: 'TASK STATUS', color: 'brand.violet' },
                  { from: 'NESTED TASK', to: 'SUBTASK LAYER', color: 'brand.cyan' },
                ].map((row, i) => (
                  <Flex key={i} alignItems="center" gap={3}>
                    <Flex flex={1} px={4} py={3} borderRadius="lg" bg="landing.surface" borderLeftWidth="3px" borderLeftStyle="solid" borderLeftColor={row.color} alignItems="center" justifyContent="center" boxShadow="sm">
                       <Text fontSize="11px" fontWeight="800" color="text.main" letterSpacing="0.02em">{row.from}</Text>
                    </Flex>
                    <Box color="text.muted" display="inline-flex"><ArrowRight size={16} strokeWidth={3} /></Box>
                    <Flex flex={1} px={4} py={3} borderRadius="lg" bg="landing.surface" borderRightWidth="3px" borderRightStyle="solid" borderRightColor={row.color} alignItems="center" justifyContent="center" boxShadow="sm">
                       <Text fontSize="11px" fontWeight="800" color="text.main" letterSpacing="0.02em">{row.to}</Text>
                    </Flex>
                  </Flex>
                ))}
              </Flex>

              <Flex mt={8} pt={6} borderTopWidth="1px" borderColor="landing.border" alignItems="center" gap={3}>
                 <Box color="success.600" display="inline-flex"><Check size={20} strokeWidth={3} /></Box>
                 <Text fontSize="sm" fontWeight="700" color="text.secondary">
                    Built on a reusable sync foundation
                 </Text>
              </Flex>
            </Box>
          </motion.div>
        </Flex>
      </Box>

      <style>{`
        @keyframes pulse-ring-scale {
          0% { transform: scale(1); opacity: 0.1; }
          50% { transform: scale(1.05); opacity: 0.3; }
          100% { transform: scale(1); opacity: 0.1; }
        }
      `}</style>
    </Box>
  );
};

export default Infographic;
